"use client";

import { useTranslations } from "@i18n/react";
import { usePlayer } from "@packages/media";
import { useToast } from "@packages/ui";
import { useEffect } from "react";

/** The notice describes the skip just attempted; a limit or a throttle stops being true soon after. */
const MAX_HOLD_MS = 120_000;

export const useSkipErrorToast = () => {
  const t = useTranslations();
  const toast = useToast();
  const player = usePlayer();
  const skipError = player?.skipError;

  useEffect(() => {
    if (!skipError) return;

    const show = () => {
      toast(
        skipError.type === "message"
          ? skipError.message
          : t(
              skipError.type === "dailyLimit"
                ? "music_radio_used_all_skips_day"
                : "music_radio_used_all_skips_hour",
            ),
      );
      player?.dismissSkipError();
    };

    const deliver = () => {
      if (Date.now() - skipError.raisedAt > MAX_HOLD_MS) {
        player?.dismissSkipError();
        return;
      }
      show();
    };

    /** A lock-screen skip lands on a hidden tab, and unlocking back into it fires focus, not visibilitychange. */
    if (document.visibilityState === "visible") {
      deliver();
      return;
    }

    const stopWaiting = () => {
      document.removeEventListener("visibilitychange", onReturn);
      window.removeEventListener("focus", onReturn);
    };

    const onReturn = () => {
      if (document.visibilityState !== "visible") return;
      stopWaiting();
      deliver();
    };

    document.addEventListener("visibilitychange", onReturn);
    window.addEventListener("focus", onReturn);

    return stopWaiting;
  }, [skipError]);
};
